// AI opponents for Zimbabwean Standard Tsoro.
//
// Three deterministic-ish difficulties:
//   - beginner:     random legal move, lightly biased toward immediate captures.
//   - intermediate: 2-ply lookahead with simple heuristic.
//   - advanced:     iterative-deepening minimax with alpha-beta, depth 4-5.
//
// All AI uses only the public engine API. It never reads hidden state and
// never bypasses isLegalMove / applyMove.

import {
  applyMove,
  getLegalMoves,
  countersOnBoardOf,
  opponentOf,
  innerRowOf,
  oppInnerOf,
  oppOuterOf,
  getPhaseFor,
} from "./engine";

function staticEval(state, perspective) {
  const opp = opponentOf(perspective);
  if (state.status !== "ACTIVE") {
    if (state.winner === perspective) return 100000;
    if (state.winner === opp) return -100000;
    return 0;
  }
  const capDiff = state.captures[perspective] - state.captures[opp];
  const myBoard = countersOnBoardOf(state.board, perspective);
  const oppBoard = countersOnBoardOf(state.board, opp);
  // Capture value dominates. On-board diff is secondary.
  let score = capDiff * 10 + (myBoard - oppBoard) * 1;

  // Mobility (only at non-leaf nodes is this called).
  const myProbe = { ...state, currentPlayer: perspective, phase: getPhaseFor(state.board, perspective) };
  const oppProbe = { ...state, currentPlayer: opp, phase: getPhaseFor(state.board, opp) };
  const myMoves = getLegalMoves(myProbe).length;
  const oppMoves = getLegalMoves(oppProbe).length;
  score += (myMoves - oppMoves) * 0.5;

  // Exposure penalty: opponent could capture my paired column.
  // For each col, if my inner pit is empty and my outer pit has counters,
  // opponent landing there would capture both → penalty for me.
  const myInner = innerRowOf(perspective);
  const myOuter = perspective === "A" ? 3 : 0;
  const oppInner = oppInnerOf(perspective);
  const oppOuter = oppOuterOf(perspective);
  for (let c = 0; c < 8; c++) {
    // my exposed column
    if (state.board[myInner][c] === 0 && state.board[myOuter][c] > 0) {
      // light penalty proportional to what could be captured
      // (only counts as a real risk if opponent has a way to land there; cheap heuristic)
      score -= state.board[myOuter][c] * 0.2;
    }
    // opp exposed column → reward
    if (state.board[oppInner][c] === 0 && state.board[oppOuter][c] > 0) {
      score += state.board[oppOuter][c] * 0.2;
    }
  }

  return score;
}

function orderMoves(state, moves) {
  // Simulate each move; sort by immediate capture (descending).
  const scored = moves.map((m) => {
    try {
      const { state: next } = applyMove(state, m[0], m[1]);
      const gain = next.captures[state.currentPlayer] - state.captures[state.currentPlayer];
      return { m, gain };
    } catch (e) {
      return { m, gain: -1 };
    }
  });
  scored.sort((a, b) => b.gain - a.gain);
  return scored.map((s) => s.m);
}

function minimax(state, depth, alpha, beta, maximizing, perspective) {
  if (depth === 0 || state.status !== "ACTIVE") {
    return { score: staticEval(state, perspective), move: null };
  }
  const moves = getLegalMoves(state);
  if (moves.length === 0) {
    return { score: staticEval(state, perspective), move: null };
  }
  const ordered = orderMoves(state, moves);
  let bestMove = ordered[0];
  if (maximizing) {
    let best = -Infinity;
    for (const m of ordered) {
      const { state: next } = applyMove(state, m[0], m[1]);
      const { score } = minimax(next, depth - 1, alpha, beta, false, perspective);
      if (score > best) {
        best = score;
        bestMove = m;
      }
      alpha = Math.max(alpha, best);
      if (beta <= alpha) break;
    }
    return { score: best, move: bestMove };
  } else {
    let best = Infinity;
    for (const m of ordered) {
      const { state: next } = applyMove(state, m[0], m[1]);
      const { score } = minimax(next, depth - 1, alpha, beta, true, perspective);
      if (score < best) {
        best = score;
        bestMove = m;
      }
      beta = Math.min(beta, best);
      if (beta <= alpha) break;
    }
    return { score: best, move: bestMove };
  }
}

function pickBeginner(state) {
  const moves = getLegalMoves(state);
  if (moves.length === 0) return null;
  // 70% pick a capture move if any captures available; else random.
  const capMoves = moves.filter((m) => {
    try {
      const { state: next } = applyMove(state, m[0], m[1]);
      return next.captures[state.currentPlayer] > state.captures[state.currentPlayer];
    } catch (e) {
      return false;
    }
  });
  if (capMoves.length > 0 && Math.random() < 0.7) {
    return capMoves[Math.floor(Math.random() * capMoves.length)];
  }
  return moves[Math.floor(Math.random() * moves.length)];
}

function pickIntermediate(state) {
  const perspective = state.currentPlayer;
  const moves = getLegalMoves(state);
  if (moves.length === 0) return null;
  const { move } = minimax(state, 2, -Infinity, Infinity, true, perspective);
  return move || moves[0];
}

function pickAdvanced(state) {
  const perspective = state.currentPlayer;
  const moves = getLegalMoves(state);
  if (moves.length === 0) return null;
  // Iterative deepening with a soft time bound.
  const start = Date.now();
  const SOFT_BUDGET_MS = 700;
  let best = moves[0];
  for (let depth = 2; depth <= 6; depth++) {
    const { move } = minimax(state, depth, -Infinity, Infinity, true, perspective);
    if (move) best = move;
    if (Date.now() - start > SOFT_BUDGET_MS) break;
  }
  return best;
}

export function chooseAIMove(state, difficulty = "intermediate") {
  if (state.status !== "ACTIVE") return null;
  switch (difficulty) {
    case "beginner":
      return pickBeginner(state);
    case "advanced":
      return pickAdvanced(state);
    case "intermediate":
    default:
      return pickIntermediate(state);
  }
}
